import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ArrowLeft,
    Calendar,
    Check,
    ChevronRight,
    GraduationCap,
    Loader,
    Trash2,
    Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import ConfirmModal from '../../ui/ConfirmModal';

function todayRomeDateString() {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Rome',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date());
}

function formatSessionDate(sessionDate) {
    if (!sessionDate) return '';
    const d = new Date(`${String(sessionDate).slice(0, 10)}T12:00:00`);
    return d.toLocaleDateString('it-IT', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });
}

function formatTime(time) {
    return time ? String(time).slice(0, 5) : '';
}

function sessionHasCap(session) {
    const max = session.max_participants;
    return max != null && max !== '' && Number(max) > 0;
}

function isSessionFull(session) {
    if (!sessionHasCap(session)) return false;
    const offers = Number(session.active_offers || 0);
    return Number(session.current_participants || 0) + offers >= Number(session.max_participants);
}

function CourseLogo({ course, size = 'md' }) {
    const sizeClass = size === 'sm' ? 'h-9 w-9' : 'h-11 w-11';
    if (course?.logo_filename) {
        return (
            <img
                src={`/api/serve_image.php?filename=${course.logo_filename}&type=logo`}
                alt=""
                className={`${sizeClass} shrink-0 rounded-lg border border-gray-200 object-cover dark:border-white/10`}
            />
        );
    }
    return (
        <span
            className={`${size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} shrink-0 rounded-full`}
            style={{ backgroundColor: course?.color || '#3B82F6' }}
            aria-hidden
        />
    );
}

function CourseRow({ course, subtitle, badge, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50/50 dark:border-white/10 dark:bg-gray-900/40 dark:hover:border-indigo-500/40 dark:hover:bg-indigo-950/20"
        >
            <CourseLogo course={course} />
            <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                        {course.title}
                    </span>
                    {badge}
                </span>
                {subtitle ? (
                    <span className="mt-0.5 block text-xs text-gray-500 dark:text-white/55">
                        {subtitle}
                    </span>
                ) : null}
            </span>
            <ChevronRight size={18} className="shrink-0 text-gray-400" aria-hidden />
        </button>
    );
}

export default function MemberCoursesTab({ member }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();

    const [enrollments, setEnrollments] = useState([]);
    const [memberWaitlist, setMemberWaitlist] = useState([]);
    const [courses, setCourses] = useState([]);
    const [sessionsByCourse, setSessionsByCourse] = useState({});
    const [loading, setLoading] = useState(true);

    const [detailCourseId, setDetailCourseId] = useState(null);
    const [selectedSessionIds, setSelectedSessionIds] = useState([]);
    const [sendNotification, setSendNotification] = useState(true);
    const [isEnrolling, setIsEnrolling] = useState(false);

    const [enrollmentToCancel, setEnrollmentToCancel] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    const enrolledSessionIds = useMemo(
        () => new Set(enrollments.map((e) => Number(e.session_id))),
        [enrollments]
    );

    const enrollmentBySessionId = useMemo(() => {
        const map = new Map();
        enrollments.forEach((e) => map.set(Number(e.session_id), e));
        return map;
    }, [enrollments]);

    const waitlistBySessionId = useMemo(() => {
        const map = new Map();
        memberWaitlist.forEach((row) => map.set(Number(row.session_id), row));
        return map;
    }, [memberWaitlist]);

    const refreshAll = useCallback(
        async ({ silent = false } = {}) => {
            try {
                if (!silent) setLoading(true);
                const api = getAuthenticatedApi();

                const [enrollRes, coursesRes] = await Promise.all([
                    api.get(`/gym_courses.php?action=list_user_enrollments&user_id=${member.id}`),
                    api.get('/gym_courses.php?action=list_courses'),
                ]);

                setEnrollments(enrollRes.data.enrollments || []);
                setMemberWaitlist(enrollRes.data.waitlist || []);
                const coursesData = (coursesRes.data.courses || []).filter(
                    (c) => c.status === 'active'
                );
                setCourses(coursesData);

                const today = new Date();
                const months = [];
                for (let i = 0; i < 6; i += 1) {
                    const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
                    months.push(
                        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                    );
                }

                const sessionsResponses = await Promise.all(
                    months.map((month) =>
                        api.get(`/gym_courses.php?action=list_sessions&month=${month}`)
                    )
                );
                const allSessions = sessionsResponses.flatMap((r) => r.data.sessions || []);
                const todayStr = todayRomeDateString();
                const grouped = {};

                allSessions.forEach((session) => {
                    if (session.session_date < todayStr) return;
                    if (!grouped[session.course_id]) grouped[session.course_id] = [];
                    grouped[session.course_id].push(session);
                });

                Object.keys(grouped).forEach((courseId) => {
                    grouped[courseId].sort(
                        (a, b) => new Date(a.session_date) - new Date(b.session_date)
                    );
                });

                setSessionsByCourse(grouped);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Errore caricamento tab corsi:', err);
                toast.error(t('gym.membersManager.courses.loadCoursesError'));
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [getAuthenticatedApi, member.id, t]
    );

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    const courseById = useMemo(() => {
        const map = new Map();
        courses.forEach((c) => map.set(Number(c.id), c));
        enrollments.forEach((e) => {
            if (!map.has(Number(e.course_id))) {
                map.set(Number(e.course_id), {
                    id: e.course_id,
                    title: e.course_title,
                    color: e.color,
                    logo_filename: e.logo_filename,
                    category: e.category,
                    status: 'active',
                });
            }
        });
        return map;
    }, [courses, enrollments]);

    const enrolledCourseIds = useMemo(() => {
        const ids = new Set();
        enrollments.forEach((e) => ids.add(Number(e.course_id)));
        return ids;
    }, [enrollments]);

    const enrolledCourses = useMemo(() => {
        return [...enrolledCourseIds]
            .map((id) => courseById.get(id))
            .filter(Boolean)
            .sort((a, b) => String(a.title).localeCompare(String(b.title), 'it'));
    }, [enrolledCourseIds, courseById]);

    const availableCourses = useMemo(() => {
        return courses
            .filter((c) => !enrolledCourseIds.has(Number(c.id)))
            .filter((c) => (sessionsByCourse[c.id] || []).length > 0)
            .sort((a, b) => String(a.title).localeCompare(String(b.title), 'it'));
    }, [courses, enrolledCourseIds, sessionsByCourse]);

    const isCourseFull = useCallback(
        (courseId) => {
            const sessions = sessionsByCourse[courseId] || [];
            if (sessions.length === 0) return false;
            const free = sessions.filter(
                (s) => !isSessionFull(s) && !enrolledSessionIds.has(Number(s.id))
            );
            return free.length === 0;
        },
        [sessionsByCourse, enrolledSessionIds]
    );

    const detailCourse = detailCourseId ? courseById.get(Number(detailCourseId)) : null;
    const detailSessions = detailCourseId ? sessionsByCourse[detailCourseId] || [] : [];
    const detailIsEnrolledCourse = detailCourseId
        ? enrolledCourseIds.has(Number(detailCourseId))
        : false;

    const openCourseDetail = (courseId) => {
        setDetailCourseId(courseId);
        setSelectedSessionIds([]);
    };

    const closeCourseDetail = () => {
        setDetailCourseId(null);
        setSelectedSessionIds([]);
    };

    const selectableSessionIds = useMemo(
        () =>
            detailSessions
                .filter((s) => {
                    const id = Number(s.id);
                    if (enrolledSessionIds.has(id) || waitlistBySessionId.has(id)) return false;
                    if (!isSessionFull(s)) return true;
                    return sessionHasCap(s);
                })
                .map((s) => s.id),
        [detailSessions, enrolledSessionIds, waitlistBySessionId]
    );

    const handleSessionToggle = (sessionId) => {
        const session = detailSessions.find((s) => s.id === sessionId);
        if (
            !session
            || enrolledSessionIds.has(Number(sessionId))
            || waitlistBySessionId.has(Number(sessionId))
        ) {
            return;
        }
        if (isSessionFull(session) && !sessionHasCap(session)) {
            return;
        }
        setSelectedSessionIds((prev) =>
            prev.includes(sessionId)
                ? prev.filter((id) => id !== sessionId)
                : [...prev, sessionId]
        );
    };

    const handleSelectAll = () => {
        const allSelected =
            selectableSessionIds.length > 0 &&
            selectableSessionIds.every((id) => selectedSessionIds.includes(id));
        if (allSelected) {
            setSelectedSessionIds([]);
        } else {
            setSelectedSessionIds([...selectableSessionIds]);
        }
    };

    const handleRemoveWaitlist = async (row) => {
        try {
            const api = getAuthenticatedApi();
            await api.post('/gym_courses.php?action=remove_from_waitlist', {
                waitlist_id: row.waitlist_id,
            });
            toast.success(t('gym.membersManager.courses.removeWaitlistSuccess'));
            await refreshAll({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.courses.waitlistError'));
        }
    };

    const handleEnroll = async () => {
        if (!selectedSessionIds.length) {
            toast.error(t('gym.membersManager.courses.selectAtLeastOneSession'));
            return;
        }

        const enrollIds = [];
        const waitlistIds = [];
        selectedSessionIds.forEach((id) => {
            const session = detailSessions.find((s) => s.id === id);
            if (session && isSessionFull(session)) waitlistIds.push(id);
            else enrollIds.push(id);
        });

        if (enrollIds.length) {
            const confirmed = window.confirm(
                t('gym.membersManager.courses.confirmEnroll', {
                    count: enrollIds.length,
                    name: member.name,
                })
            );
            if (!confirmed) return;
        }
        if (waitlistIds.length) {
            const confirmed = window.confirm(
                t('gym.membersManager.courses.confirmWaitlist', {
                    count: waitlistIds.length,
                    name: member.name,
                })
            );
            if (!confirmed) return;
        }

        setIsEnrolling(true);
        try {
            const api = getAuthenticatedApi();
            let response = { data: { success: true, total_enrollments_created: 0, duplicates_skipped: 0, errors: [] } };
            if (enrollIds.length) {
                response = await api.post('/gym_courses.php', {
                    action: 'bulk_enroll_multiple_sessions',
                    user_ids: [member.id],
                    session_ids: enrollIds,
                    send_notification: sendNotification,
                });
            }
            if (waitlistIds.length) {
                const waitResponse = await api.post('/gym_courses.php?action=add_to_waitlist', {
                    user_ids: [member.id],
                    session_ids: waitlistIds,
                });
                const added = waitResponse.data.added_count || 0;
                if (added > 0) {
                    toast.success(t('gym.membersManager.courses.waitlistSuccess', { count: added }));
                }
                const waitErrors = waitResponse.data.errors || [];
                if (waitErrors.length > 0) {
                    toast.error(waitErrors[0]);
                }
            }

            if (response.data.success) {
                const created = response.data.total_enrollments_created || 0;
                const duplicates = response.data.duplicates_skipped || 0;
                const errors = response.data.errors || [];

                if (created > 0) {
                    toast.success(
                        t('gym.membersManager.courses.enrollSuccess', { count: created })
                    );
                }
                if (duplicates > 0) {
                    toast(
                        t('gym.membersManager.courses.enrollDuplicates', { count: duplicates }),
                        { icon: 'ℹ️' }
                    );
                }
                if (errors.length > 0) {
                    toast.error(
                        `${t('gym.membersManager.courses.enrollPartialErrors', {
                            count: errors.length,
                        })}: ${errors[0]}`
                    );
                }
                if (
                    enrollIds.length > 0
                    && created === 0
                    && duplicates === 0
                    && errors.length === 0
                ) {
                    toast.error(t('gym.membersManager.courses.enrollNone'));
                }

                setSelectedSessionIds([]);
                await refreshAll({ silent: true });
            } else {
                toast.error(
                    response.data.error || t('gym.membersManager.courses.enrollError')
                );
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Errore iscrizione corsi:', err);
            toast.error(
                err.response?.data?.error || t('gym.membersManager.courses.enrollError')
            );
        } finally {
            setIsEnrolling(false);
        }
    };

    const handleConfirmCancel = async () => {
        if (!enrollmentToCancel) return;
        setCancelling(true);
        try {
            const api = getAuthenticatedApi();
            const response = await api.post('/gym_courses.php', {
                action: 'cancel_enrollment',
                enrollment_id: enrollmentToCancel.enrollment_id,
                send_notification: true,
            });
            if (response.data.success) {
                toast.success(t('gym.membersManager.courses.cancelSuccess'));
                setEnrollmentToCancel(null);
                await refreshAll({ silent: true });
            } else {
                toast.error(
                    response.data.error || t('gym.membersManager.courses.cancelError')
                );
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Errore cancellazione iscrizione:', err);
            toast.error(
                err.response?.data?.error || t('gym.membersManager.courses.cancelError')
            );
        } finally {
            setCancelling(false);
        }
    };

    const allSelectableSelected =
        selectableSessionIds.length > 0 &&
        selectableSessionIds.every((id) => selectedSessionIds.includes(id));

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <LoadingSpinner />
            </div>
        );
    }

    // Dettaglio ricorrenze di un corso
    if (detailCourse) {
        const enrollmentCount = detailSessions.filter((s) =>
            enrolledSessionIds.has(Number(s.id))
        ).length;

        return (
            <div className="space-y-4">
                <div className="flex items-start gap-3">
                    <button
                        type="button"
                        onClick={closeCourseDetail}
                        className="mt-0.5 inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-white/75 dark:hover:bg-white/10"
                    >
                        <ArrowLeft size={16} aria-hidden />
                        {t('gym.membersManager.courses.backToList')}
                    </button>
                </div>

                <section className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/40">
                    <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/90 px-3 py-3 dark:border-white/5 dark:bg-gray-900/60">
                        <CourseLogo course={detailCourse} />
                        <div className="min-w-0 flex-1">
                            <h4 className="truncate text-base font-semibold text-gray-900 dark:text-white">
                                {detailCourse.title}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-white/55">
                                {detailIsEnrolledCourse
                                    ? t('gym.membersManager.courses.detailEnrolledSubtitle', {
                                          count: enrollmentCount,
                                      })
                                    : t('gym.membersManager.courses.detailAvailableSubtitle', {
                                          count: detailSessions.length,
                                      })}
                            </p>
                        </div>
                        {detailIsEnrolledCourse ? (
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                {t('gym.membersManager.courses.badgeEnrolled')}
                            </span>
                        ) : isCourseFull(detailCourse.id) ? (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                {t('gym.membersManager.courses.badgeFull')}
                            </span>
                        ) : null}
                    </div>

                    <div className="p-3">
                        {detailSessions.length === 0 ? (
                            <p className="py-6 text-center text-sm text-gray-500 dark:text-white/60">
                                {t('gym.membersManager.courses.noFutureSessions')}
                            </p>
                        ) : (
                            <>
                                {selectableSessionIds.length > 0 && (
                                    <div className="mb-2 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={handleSelectAll}
                                            className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                                        >
                                            {allSelectableSelected
                                                ? t('gym.membersManager.courses.deselectAllSessions')
                                                : t('gym.membersManager.courses.selectAllSessions')}
                                        </button>
                                    </div>
                                )}

                                <ul className="max-h-[28rem] space-y-1.5 overflow-y-auto">
                                    {detailSessions.map((session) => {
                                        const full = isSessionFull(session);
                                        const enrollment = enrollmentBySessionId.get(
                                            Number(session.id)
                                        );
                                        const waitEntry = waitlistBySessionId.get(Number(session.id));
                                        const alreadyEnrolled = Boolean(enrollment);
                                        const alreadyWaiting = Boolean(waitEntry);
                                        const checked = selectedSessionIds.includes(session.id);
                                        const canSelect = !alreadyEnrolled && !alreadyWaiting && (!full || sessionHasCap(session));

                                        return (
                                            <li
                                                key={session.id}
                                                className={`rounded-lg border px-3 py-2.5 ${
                                                    alreadyEnrolled
                                                        ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-500/30 dark:bg-emerald-950/20'
                                                        : checked
                                                          ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-500/40 dark:bg-indigo-950/30'
                                                          : full
                                                            ? 'border-amber-200 bg-amber-50/40 dark:border-amber-500/20 dark:bg-amber-950/10'
                                                            : 'border-gray-200 dark:border-white/10'
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    {canSelect ? (
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() =>
                                                                handleSessionToggle(session.id)
                                                            }
                                                            className="mt-1 h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
                                                            aria-label={formatSessionDate(
                                                                session.session_date
                                                            )}
                                                        />
                                                    ) : alreadyEnrolled || alreadyWaiting ? (
                                                        <Check
                                                            size={16}
                                                            className="mt-1 shrink-0 text-emerald-600 dark:text-emerald-400"
                                                            aria-hidden
                                                        />
                                                    ) : (
                                                        <span className="mt-1 h-4 w-4 shrink-0" />
                                                    )}

                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                                                            <Calendar
                                                                size={14}
                                                                className="text-gray-400"
                                                                aria-hidden
                                                            />
                                                            <span className="font-medium text-gray-900 dark:text-white">
                                                                {formatSessionDate(
                                                                    session.session_date
                                                                )}
                                                            </span>
                                                            <span className="text-gray-600 dark:text-white/65">
                                                                {formatTime(session.start_time)}
                                                                {' – '}
                                                                {formatTime(session.end_time)}
                                                            </span>
                                                        </div>
                                                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-white/55">
                                                            <span className="inline-flex items-center gap-1">
                                                                <Users size={12} aria-hidden />
                                                                {session.current_participants || 0}/
                                                                {session.max_participants || '∞'}
                                                            </span>
                                                            {alreadyEnrolled && (
                                                                <span className="font-medium text-emerald-700 dark:text-emerald-300">
                                                                    {t(
                                                                        'gym.membersManager.courses.alreadyEnrolled'
                                                                    )}
                                                                </span>
                                                            )}
                                                            {full && !alreadyEnrolled && !alreadyWaiting && (
                                                                <span className="font-medium text-amber-700 dark:text-amber-300">
                                                                    {t(
                                                                        'gym.membersManager.courses.sessionFull'
                                                                    )}
                                                                </span>
                                                            )}
                                                            {alreadyWaiting && (
                                                                <span className="font-medium text-amber-700 dark:text-amber-300">
                                                                    {t('gym.membersManager.courses.inWaitlist')}
                                                                    {waitEntry.position
                                                                        ? ` (n° ${waitEntry.position})`
                                                                        : ''}
                                                                </span>
                                                            )}
                                                            {session.location ? (
                                                                <span>{session.location}</span>
                                                            ) : null}
                                                        </div>
                                                    </div>

                                                    {alreadyWaiting && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveWaitlist(waitEntry)}
                                                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-950/40"
                                                        >
                                                            <Trash2 size={14} aria-hidden />
                                                            <span className="hidden sm:inline">
                                                                {t('gym.membersManager.courses.removeWaitlist')}
                                                            </span>
                                                        </button>
                                                    )}
                                                    {alreadyEnrolled && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setEnrollmentToCancel(enrollment)
                                                            }
                                                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40"
                                                            title={t(
                                                                'gym.membersManager.courses.cancelEnrollment'
                                                            )}
                                                        >
                                                            <Trash2 size={14} aria-hidden />
                                                            <span className="hidden sm:inline">
                                                                {t(
                                                                    'gym.membersManager.courses.cancelEnrollment'
                                                                )}
                                                            </span>
                                                        </button>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>

                                {selectableSessionIds.length > 0 && (
                                    <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-3 dark:border-white/5 sm:flex-row sm:items-center sm:justify-between">
                                        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-white/75">
                                            <input
                                                type="checkbox"
                                                checked={sendNotification}
                                                onChange={(e) =>
                                                    setSendNotification(e.target.checked)
                                                }
                                                className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
                                            />
                                            {t('gym.membersManager.courses.sendNotifications')}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleEnroll}
                                            disabled={
                                                isEnrolling || selectedSessionIds.length === 0
                                            }
                                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                                        >
                                            {isEnrolling ? (
                                                <Loader
                                                    size={16}
                                                    className="animate-spin"
                                                    aria-hidden
                                                />
                                            ) : (
                                                <GraduationCap size={16} aria-hidden />
                                            )}
                                            {(() => {
                                                const waitCount = selectedSessionIds.filter((id) => {
                                                    const session = detailSessions.find((s) => s.id === id);
                                                    return session && isSessionFull(session);
                                                }).length;
                                                const enrollCount = selectedSessionIds.length - waitCount;
                                                if (enrollCount > 0 && waitCount > 0) {
                                                    return t('gym.membersManager.courses.enrollAndWaitlistButton', {
                                                        enroll: enrollCount,
                                                        waitlist: waitCount,
                                                    });
                                                }
                                                if (waitCount > 0) {
                                                    return t('gym.membersManager.courses.waitlistButton', {
                                                        count: waitCount,
                                                    });
                                                }
                                                return t('gym.membersManager.courses.confirmButton', {
                                                    count: enrollCount,
                                                });
                                            })()}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </section>

                <ConfirmModal
                    isOpen={Boolean(enrollmentToCancel)}
                    onClose={() => {
                        if (cancelling) return;
                        setEnrollmentToCancel(null);
                    }}
                    onConfirm={handleConfirmCancel}
                    title={t('gym.membersManager.courses.cancelConfirmTitle')}
                    message={
                        enrollmentToCancel
                            ? t('gym.membersManager.courses.cancelConfirmMessage', {
                                  course: enrollmentToCancel.course_title,
                                  date: formatSessionDate(enrollmentToCancel.session_date),
                                  time: formatTime(enrollmentToCancel.start_time),
                              })
                            : ''
                    }
                    confirmText={t('gym.membersManager.courses.cancelConfirmButton')}
                    cancelText={t('gym.editExtendedProfile.actions.cancel', {
                        defaultValue: 'Annulla',
                    })}
                    variant="danger"
                    loading={cancelling}
                />
            </div>
        );
    }

    // Lista: iscritti sopra, disponibili sotto
    return (
        <div className="space-y-5">
            <section className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/40">
                <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/90 px-3 py-2.5 dark:border-white/5 dark:bg-gray-900/60">
                    <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
                        <Check size={15} className="text-emerald-600 dark:text-emerald-400" aria-hidden />
                        {t('gym.membersManager.courses.enrolledCoursesTitle')}
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-white/55">
                        {t('gym.membersManager.courses.coursesCount', {
                            count: enrolledCourses.length,
                        })}
                    </span>
                </div>
                <div className="space-y-2 p-3">
                    {enrolledCourses.length === 0 ? (
                        <p className="py-4 text-center text-sm text-gray-500 dark:text-white/60">
                            {t('gym.membersManager.courses.noEnrolledCourses')}
                        </p>
                    ) : (
                        enrolledCourses.map((course) => {
                            const count = enrollments.filter(
                                (e) => Number(e.course_id) === Number(course.id)
                            ).length;
                            return (
                                <CourseRow
                                    key={course.id}
                                    course={course}
                                    subtitle={t(
                                        'gym.membersManager.courses.enrolledSessionsCount',
                                        { count }
                                    )}
                                    badge={
                                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                            {t('gym.membersManager.courses.badgeEnrolled')}
                                        </span>
                                    }
                                    onClick={() => openCourseDetail(course.id)}
                                />
                            );
                        })
                    )}
                </div>
            </section>

            <section className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/40">
                <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/90 px-3 py-2.5 dark:border-white/5 dark:bg-gray-900/60">
                    <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
                        <GraduationCap
                            size={15}
                            className="text-indigo-600 dark:text-indigo-400"
                            aria-hidden
                        />
                        {t('gym.membersManager.courses.availableCoursesTitle')}
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-white/55">
                        {t('gym.membersManager.courses.coursesCount', {
                            count: availableCourses.length,
                        })}
                    </span>
                </div>
                <div className="space-y-2 p-3">
                    {availableCourses.length === 0 ? (
                        <p className="py-4 text-center text-sm text-gray-500 dark:text-white/60">
                            {t('gym.membersManager.courses.noAvailableCourses')}
                        </p>
                    ) : (
                        availableCourses.map((course) => {
                            const sessions = sessionsByCourse[course.id] || [];
                            const full = isCourseFull(course.id);
                            return (
                                <CourseRow
                                    key={course.id}
                                    course={course}
                                    subtitle={t(
                                        'gym.membersManager.courses.futureSessionsCount',
                                        { count: sessions.length }
                                    )}
                                    badge={
                                        full ? (
                                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                                {t('gym.membersManager.courses.badgeFull')}
                                            </span>
                                        ) : null
                                    }
                                    onClick={() => openCourseDetail(course.id)}
                                />
                            );
                        })
                    )}
                </div>
            </section>
        </div>
    );
}
